import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Container, Typography, Box, Grid } from '@mui/material';
import { FormErrorProvider } from '@/providers/FormErrorContext';
import AccountContactForm from '../AccountContactForm';
import { USER_FETCH } from '@/providers/actionTypes';
import NewUserDesktopWizard from './NewUserDesktopWizard';
import NewUserMobileWizard from './NewUserMobileWizard';
import { useUser, useUserDispatch } from '@/providers/UserContext';
import { updateUser } from '@/actions/user';
import { useApi } from '@/providers/ApiContext';

import TermsAndConditions from '../TermsAndConditions';
import LoadingMask from '@/components/shared/LoadingMask';
import { useAuth } from '@/providers/AuthContext';
import type { OnboardingStep, UserUpdate } from '@/types';

function NewUserOnboarding() {
  const [revision, setRevision] = useState<number>(0);
  const navigate = useNavigate();
  const user = useUser();
  const auth = useAuth();
  const api = useApi();

  const [userUpdate, setUserUpdate] = useState<UserUpdate>({});
  const userDispatch = useUserDispatch();

  useEffect(() => {
    setRevision((prevState) => prevState + 1);
  }, [user]);

  const handleUserUpdate = async () => {
    if (!api || !user) return;
    const payload = Object.fromEntries(
      Object.entries({ ...user, ...userUpdate }).filter(
        ([_k, v]) => v !== undefined && v !== '',
      ),
    );
    const updated_user = await updateUser(api, payload);
    userDispatch({ type: USER_FETCH, payload: updated_user });
  };

  const steps: OnboardingStep[] = [
    {
      name: 'Contact Info',
      component: (
        <AccountContactForm
          key={`account_contact_${revision}`}
          setValues={setUserUpdate}
        />
      ),
    },

    {
      name: 'Terms and Conditions',
      component: (
        <TermsAndConditions
          key={`terms_and_conditions_${revision}`}
          setValues={setUserUpdate}
        />
      ),
    },
  ];

  useEffect(() => {
    if (!auth.user) {
      navigate('/login');
    }

    if (user?.terms_version) {
      navigate('/');
    }
  }, [auth.user, user, navigate]);

  if (!user?.entity_id) {
    return <LoadingMask />;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Container sx={{ padding: '15px' }}>
        <Grid container spacing={4}>
          <Grid size={12}>
            <Typography variant='h4'>Account Signup</Typography>
          </Grid>
          <Grid size={12}>
            <FormErrorProvider>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1 }}>
                <NewUserDesktopWizard steps={steps} onNext={handleUserUpdate} />
              </Box>
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexGrow: 1 }}>
                <NewUserMobileWizard
                  steps={steps}
                  onNext={handleUserUpdate}
                />{' '}
              </Box>
            </FormErrorProvider>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default NewUserOnboarding;
